/**
 * ONE filters/facets contract for every paid surface (W-R4-2, DISC-AC-138 +
 * DISC-AC-155 c). Shared verbatim by:
 *   - the builder-service post-pass (`bigquery/dashboardRowMappers.ts` and the
 *     creative-performance attach) — the SERVER truth;
 *   - the FunnelReportsPage draft-filter instant preview — a PROJECTION of
 *     this same declared contract, never a re-implementation.
 *
 * A facet applies to a surface if and only if this table declares an accessor
 * for it. A facet declared without an accessor for a surface is IGNORED on
 * that surface (the rows genuinely lack the dimension — e.g. creative
 * performance rows are creative-grain delivery aggregates with no ad-set and
 * no session match quality), never silently matched against a missing field.
 *
 * Matching semantics (the single implementation): case-insensitive substring
 * per facet, AND across active facets.
 */

export const PAID_SURFACES = ['campaignEconomics', 'creativeDiagnostics', 'creativePerformance'] as const;
export type PaidSurface = (typeof PAID_SURFACES)[number];

export const PAID_FACET_KEYS = [
  'campaign',
  'metaAdAccountId',
  'metaCampaignId',
  'metaAdsetId',
  'metaAdId',
  'metaCreativeId',
  'matchQuality',
] as const;
export type PaidFacetKey = (typeof PAID_FACET_KEYS)[number];

export type PaidFacetFilters = Partial<Record<PaidFacetKey, string | undefined>>;

/** Minimal structural row per surface — the fields the contract can read. */
export interface PaidEconomicsLikeRow {
  campaign: string;
  metaAdAccountId?: string;
  metaCampaignId?: string;
  metaAdsetId?: string;
  metaAdId?: string;
  metaCreativeId?: string;
  matchQuality?: string;
}

export interface PaidPerformanceLikeRow {
  campaignName?: string;
  adAccountId?: string;
  campaignId?: string;
  adId?: string;
  creativeId?: string;
}

type FacetAccessor<Row> = (row: Row) => string | undefined;

interface PaidFacetDeclaration {
  campaignEconomics?: FacetAccessor<PaidEconomicsLikeRow>;
  creativeDiagnostics?: FacetAccessor<PaidEconomicsLikeRow>;
  creativePerformance?: FacetAccessor<PaidPerformanceLikeRow>;
}

/**
 * The declared applies/not-applied table (DISC-AC-138). Absent accessor =
 * facet not applicable on that surface, by declaration.
 */
export const PAID_FACET_CONTRACT: Record<PaidFacetKey, PaidFacetDeclaration> = {
  campaign: {
    campaignEconomics: row => row.campaign,
    creativeDiagnostics: row => row.campaign,
    creativePerformance: row => row.campaignName,
  },
  metaAdAccountId: {
    campaignEconomics: row => row.metaAdAccountId,
    creativeDiagnostics: row => row.metaAdAccountId,
    creativePerformance: row => row.adAccountId,
  },
  metaCampaignId: {
    campaignEconomics: row => row.metaCampaignId,
    creativeDiagnostics: row => row.metaCampaignId,
    creativePerformance: row => row.campaignId,
  },
  // Ad-set exists only where the session match cascade resolved it — creative
  // performance rows are creative-grain aggregates ACROSS ad sets.
  metaAdsetId: {
    campaignEconomics: row => row.metaAdsetId,
    creativeDiagnostics: row => row.metaAdsetId,
  },
  metaAdId: {
    campaignEconomics: row => row.metaAdId,
    creativeDiagnostics: row => row.metaAdId,
    creativePerformance: row => row.adId,
  },
  metaCreativeId: {
    campaignEconomics: row => row.metaCreativeId,
    creativeDiagnostics: row => row.metaCreativeId,
    creativePerformance: row => row.creativeId,
  },
  // Pure Meta delivery aggregates carry no session match — declared not-applied.
  matchQuality: {
    campaignEconomics: row => row.matchQuality,
    creativeDiagnostics: row => row.matchQuality,
  },
};

/** The single matching primitive: case-insensitive substring; empty filter passes. */
export function paidFacetValueMatches(value: string | undefined, filter: string | undefined): boolean {
  const normalizedFilter = filter?.trim().toLowerCase();
  if (!normalizedFilter) return true;
  return (value ?? '').toLowerCase().includes(normalizedFilter);
}

export function hasActivePaidFacetFilters(filters: PaidFacetFilters | undefined): boolean {
  if (!filters) return false;
  return PAID_FACET_KEYS.some(key => Boolean(filters[key]?.trim()));
}

type SurfaceRow<S extends PaidSurface> = S extends 'creativePerformance' ? PaidPerformanceLikeRow : PaidEconomicsLikeRow;

export function paidRowMatchesFacets<S extends PaidSurface>(
  surface: S,
  row: SurfaceRow<S>,
  filters: PaidFacetFilters,
): boolean {
  return PAID_FACET_KEYS.every(key => {
    const accessor = PAID_FACET_CONTRACT[key][surface] as FacetAccessor<SurfaceRow<S>> | undefined;
    if (!accessor) return true; // declared not-applied on this surface
    return paidFacetValueMatches(accessor(row), filters[key]);
  });
}

export function applyPaidFacetFilters<S extends PaidSurface, T extends SurfaceRow<S>>(
  surface: S,
  rows: T[],
  filters: PaidFacetFilters | undefined,
): T[] {
  if (!filters || !hasActivePaidFacetFilters(filters)) return rows;
  return rows.filter(row => paidRowMatchesFacets(surface, row, filters));
}

/**
 * The full request-facet vocabulary of the `/insights/v1` report surface
 * (DISC-AC-155 c) — a SUPERSET so directory/series endpoints never fork their
 * filter contract from the split's. `funnels` follows the Creative Decisions
 * portfolio precedent; entity keys are exact-id selectors (the drilldown
 * `campaignId`), distinct from the fuzzy paid facets above.
 */
export const INSIGHTS_FACET_VOCABULARY = [
  // window + scope
  'from', 'to', 'tz', 'onboardingId', 'versionId', 'alias', 'funnels',
  // fuzzy paid facets (the contract above)
  ...PAID_FACET_KEYS,
  // exact entity keys
  'campaignId', 'adId', 'creativeId',
  // endpoint selectors + pagination + comparison
  'view', 'entityType', 'limit', 'cursor', 'compare', 'series',
] as const;
export type InsightsFacetKey = (typeof INSIGHTS_FACET_VOCABULARY)[number];
